import { useState } from "react";
import { Autocomplete } from "@base-ui/react/autocomplete";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { VehicleProfile } from "./types";
import { FormFields } from "@/components/page-canvas";

export type VehicleCatalogue = Record<string, string[]>;

export default function CatalogueFields({
  vehicle,
  catalogue,
  errors,
  inputPrefix = "vehicle",
  idPrefix = "vehicle",
  autoFocus = true,
  required = true,
  onChange,
}: {
  vehicle: Partial<Pick<VehicleProfile, "make" | "model">> | null;
  inputPrefix?: string;
  idPrefix?: string;
  autoFocus?: boolean;
  required?: boolean;
  onChange?: (field: "make" | "model", value: string) => void;
  catalogue: VehicleCatalogue;
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();
  const [make, setMake] = useState(vehicle?.make ?? "");
  const [model, setModel] = useState(vehicle?.model ?? "");
  const makes = Object.keys(catalogue);
  const selectedMake = onChange ? (vehicle?.make ?? "") : make;
  const matchedMake = makes.find(
    (name) => name.toLowerCase() === selectedMake.trim().toLowerCase(),
  );
  const models = matchedMake ? catalogue[matchedMake] : [];

  return (
    <FormFields>
      {(
        [
          {
            field: "make",
            items: makes,
            value: onChange ? (vehicle?.make ?? "") : make,
            onChange: (value: string) => {
              setMake(value);
              onChange?.("make", value);
            },
          },
          {
            field: "model",
            items: models,
            value: onChange ? (vehicle?.model ?? "") : model,
            onChange: (value: string) => {
              setModel(value);
              onChange?.("model", value);
            },
          },
        ] as const
      ).map(({ field, items, value, onChange }) => (
        <div key={field} className="space-y-2">
          <Label htmlFor={`${idPrefix}-${field}`}>
            {t(`vehicles.${field}`)}
          </Label>
          <Autocomplete.Root
            items={items}
            value={value}
            onValueChange={onChange}
            name={`${inputPrefix}[${field}]`}
            limit={50}
            openOnInputClick
          >
            <Autocomplete.Input
              id={`${idPrefix}-${field}`}
              render={<Input />}
              autoFocus={autoFocus && field === "make"}
              required={required}
              aria-required={required}
              aria-invalid={!!errors?.[field]}
              aria-describedby={
                errors[field]
                  ? `${idPrefix}-${field}-error`
                  : `${idPrefix}-catalogue-help`
              }
            />
            <Autocomplete.Portal>
              <Autocomplete.Positioner className="isolate z-50" sideOffset={4}>
                <Autocomplete.Popup className="max-h-(--available-height) w-(--anchor-width) rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10">
                  <Autocomplete.Empty className="text-muted-foreground p-3 text-sm">
                    {t("vehicles.catalogue_empty")}
                  </Autocomplete.Empty>
                  <Autocomplete.List className="max-h-60 overflow-y-auto p-1 outline-none">
                    {(item: string) => (
                      <Autocomplete.Item
                        key={item}
                        value={item}
                        className="cursor-pointer rounded-md px-2 py-1.5 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                      >
                        {item}
                      </Autocomplete.Item>
                    )}
                  </Autocomplete.List>
                </Autocomplete.Popup>
              </Autocomplete.Positioner>
            </Autocomplete.Portal>
          </Autocomplete.Root>
          {errors[field] && (
            <p
              id={`${idPrefix}-${field}-error`}
              className="text-sm text-destructive"
            >
              {errors[field]}
            </p>
          )}
        </div>
      ))}
    </FormFields>
  );
}

export function CatalogueCredit() {
  const { t } = useTranslation();
  return (
    <p className="text-muted-foreground mt-6 text-sm">
      {t("vehicles.catalogue_credit")}{" "}
      <a href="https://vehiclesdb.com" className="underline">
        VehiclesDB
      </a>
      {" · "}
      <a
        href="https://creativecommons.org/licenses/by/4.0/"
        className="underline"
      >
        CC BY 4.0
      </a>
      {" · "}
      <a
        href="/vehicle-catalogue-attribution.txt"
        target="_blank"
        rel="noopener"
        className="underline"
      >
        {t("vehicles.catalogue_notices")}
      </a>
      <span className="mt-1 block">{t("vehicles.catalogue_changes")}</span>
    </p>
  );
}
